import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  CircleDollarSign,
  Archive,
  Pause,
  Pencil,
  Play,
  Plus,
  ScanLine,
  Shield,
  Square,
  Ticket,
  Timer,
  UtensilsCrossed,
  Users,
} from 'lucide-react';
import { resourceStatusColors } from '@/lib/ui-tokens';
import { formatCurrency, formatDuration } from '@/lib/i18n';
import { usePermission } from '@/Hooks/usePermission';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { EXPIRING_SECONDS, meterFor, type ResourceRow, type SessionRow } from '../lib/sessions';
import { usePricingListQuery } from '@/Pages/Pricing/pricingListsApi';
import { PricingRuleList } from '@/Pages/Pricing/components/PricingRuleList';
import { KidsWaitingList } from './KidsWaitingList';
import { useChildDirectory, useKidsOccupancy, useWaitingList } from './kids-area';

interface ResourceCardProps {
  resource: ResourceRow;
  /**
   * Every session running on this resource. A PS5 or a billiard table has at
   * most one; a kids area seats `capacity` children at once (§11), so the card
   * has to show a list rather than a single meter.
   */
  sessions: SessionRow[];
  now: Date;
  currency: string;
  reducedMotion: boolean;
  onStart: () => void;
  onPause: (session: SessionRow) => void;
  onResume: (session: SessionRow) => void;
  onExtend: (session: SessionRow) => void;
  onStop: (session: SessionRow) => void;
  /** §11 — kids areas only: check a band out without picking the child first. */
  onCheckOutByTag?: () => void;
  /**
   * ADR-003 — order food and drink for the guest on this meter, against the
   * bill its check-in pinned. There is no table on this floor, and there does
   * not need to be: the charge lands on the meter's own check rather than
   * opening a second visit for the same child.
   */
  onOrder?: (session: SessionRow) => void;
  /** Re-read the page after the card itself changed something (seating a ticket). */
  /** C-16 — edit or retire the RESOURCE. Absent where the board is read-only. */
  onEdit?: () => void;
  onRetire?: () => void;
  /**
   * §10.2 — go to what prices this resource.
   *
   * Two destinations behind one prop, resolved by the floor map: the rate card
   * this resource charges from, or — when it charges from none — the resource's
   * own form, because `pricingListId` is a field on the resource and nowhere
   * else. The card no longer hosts a rule editor of its own; a rate belongs to
   * a card that ten consoles share, and an editor on the card would have been
   * the per-resource shape back again under a new name.
   */
  onPricing?: () => void;
  onRefresh: () => void;
  busy: boolean;
}

export function ResourceCard({
  resource,
  sessions,
  now,
  currency,
  reducedMotion,
  onStart,
  onEdit,
  onRetire,
  onPricing,
  onPause,
  onResume,
  onExtend,
  onStop,
  onCheckOutByTag,
  onOrder,
  onRefresh,
  busy,
}: ResourceCardProps) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const [showRules, setShowRules] = useState(false);

  // §11 — a kids area answers for its own capacity and queue; every other
  // resource type is fully described by the sessions already in hand.
  const isKids = resource.type === 'KIDS_AREA';
  const canViewSessions = can('resource_sessions.view');
  const { occupancy: kidsOccupancy } = useKidsOccupancy(
    resource.id,
    isKids && canViewSessions,
    sessions.length,
  );
  const queue = useWaitingList(resource.id, isKids && canViewSessions, sessions.length);
  /**
   * The children in a kids area, but only for a terminal that may read them.
   *
   * `GET /resource-sessions` stopped carrying the child when `children.view`
   * became a key of its own, and this board is a waiter's: they hold the timer
   * keys and not that one. So the names are asked for when the actor holds the
   * key and simply absent when they do not — absent, rather than replaced with
   * "no guardian on file", which would be a lie told to the one person who
   * cannot check it.
   */
  const canReadChildren = can('children.view');
  const childIds = useMemo(
    () => (isKids && canReadChildren ? sessions.map((row) => row.childId ?? '').filter(Boolean) : []),
    [isKids, canReadChildren, sessions],
  );
  const children = useChildDirectory(childIds);

  /**
   * The rate card this resource charges from, read when the disclosure is opened
   * and not before.
   *
   * One cache entry per CARD rather than per resource, which is the point of the
   * model showing through: a room of ten PlayStations on "Standard rates" makes
   * one request between them, and a rate edited in the price-list editor
   * invalidates the single entry all ten are reading. The old per-resource read
   * made ten requests and could leave nine of them quoting yesterday's rate.
   */
  const attachedList = resource.pricingList ?? null;
  const listQuery = usePricingListQuery(attachedList?.id ?? '', {
    skip: !showRules || !attachedList || !can('resources.view'),
  });

  const capacity = Math.max(1, kidsOccupancy?.capacity ?? resource.capacity);
  const shared = capacity > 1;
  // The server's count includes sessions this page has not polled yet, so it
  // wins over the local list whenever it is available.
  const occupied = kidsOccupancy?.occupied ?? sessions.length;
  const primary = sessions[0] ?? null;
  const meter = primary ? meterFor(primary, now) : null;

  const status = sessions.some((session) => session.status === 'ACTIVE')
    ? 'IN_USE'
    : sessions.length
      ? 'PAUSED'
      : resource.status;

  const expiring =
    meter?.remaining !== null && meter?.remaining !== undefined
      ? meter.remaining <= EXPIRING_SECONDS
      : false;
  const overdue = meter?.remaining === 0;

  // §11 capacity — a shared area is startable until its last place is taken.
  const startable =
    resource.isActive &&
    resource.status !== 'MAINTENANCE' &&
    resource.status !== 'OUT_OF_SERVICE' &&
    occupied < capacity;

  const progress =
    meter && meter.remaining !== null && meter.seconds + meter.remaining > 0
      ? Math.min(100, (meter.seconds / (meter.seconds + meter.remaining)) * 100)
      : null;

  const actionsFor = (session: SessionRow) => (
    <div className="flex flex-wrap gap-2">
      {/* The floor this sits on may have no tables at all, so this is the only
          way food reaches the guest on the meter. It needs the bill the meter
          was opened against — a session started with no table and no open check
          has nothing to post onto, and simply does not offer it. */}
      {onOrder && session.checkId && can('orders.create') && (
        <Button size="sm" variant="outline" onClick={() => onOrder(session)} disabled={busy}>
          <UtensilsCrossed className="h-4 w-4" />
          {t('spaces.resources.orderFood')}
        </Button>
      )}
      {session.status === 'ACTIVE' && can('resource_sessions.pause') && (
        <Button size="sm" variant="outline" onClick={() => onPause(session)} disabled={busy}>
          <Pause className="h-4 w-4" />
          {t('resource.pause')}
        </Button>
      )}
      {session.status === 'PAUSED' && can('resource_sessions.resume') && (
        <Button size="sm" variant="outline" onClick={() => onResume(session)} disabled={busy}>
          <Play className="h-4 w-4" />
          {t('resource.resume')}
        </Button>
      )}
      {can('resource_sessions.extend') && (
        <Button size="sm" variant="outline" onClick={() => onExtend(session)} disabled={busy}>
          <Plus className="h-4 w-4" />
          {t('resource.extend')}
        </Button>
      )}
      {can('resource_sessions.stop') && (
        <Button size="sm" variant="danger" onClick={() => onStop(session)} disabled={busy}>
          <Square className="h-4 w-4" />
          {shared ? t('spaces.resources.checkOut') : t('resource.stop')}
        </Button>
      )}
    </div>
  );

  return (
    <Card className={expiring && primary ? 'border-amber-400' : undefined}>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate font-medium">{resource.name}</p>
            <p className="text-xs text-ink-muted">
              {t(`spaces.resources.type.${resource.type}`, resource.type)}
              {resource.code && (
                <>
                  {' · '}
                  <span dir="ltr">{resource.code}</span>
                </>
              )}
            </p>
          </div>
          <span className="flex shrink-0 items-center gap-1.5 text-xs">
            <span
              className="h-2.5 w-2.5 rounded-full"
              style={{
                background: resourceStatusColors[status as keyof typeof resourceStatusColors],
              }}
              aria-hidden
            />
            {t(`spaces.resources.status.${status}`, status)}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-muted">
          <span className="flex items-center gap-1">
            <Users className="h-3.5 w-3.5" />
            {shared
              ? t('spaces.resources.occupancy', { used: occupied, capacity })
              : t('spaces.resources.capacity', { count: capacity })}
          </span>
          {isKids && kidsOccupancy && kidsOccupancy.free > 0 && (
            <span>{t('spaces.kids.freePlaces', { count: kidsOccupancy.free })}</span>
          )}
          {isKids && kidsOccupancy && kidsOccupancy.waitingCount > 0 && (
            <Badge tone="warning">
              {t('spaces.kids.waitingCount', { count: kidsOccupancy.waitingCount })}
            </Badge>
          )}
          {!shared && primary && primary.playerCount > 1 && (
            <span>{t('spaces.resources.players', { count: primary.playerCount })}</span>
          )}
        </div>

        {!shared && meter && primary && (
          <div className="rounded-lg bg-[var(--muted)] p-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="flex items-center gap-1.5 text-xs text-ink-muted">
                <Timer className="h-3.5 w-3.5" />
                {t('resource.elapsed')}
              </span>
              <span className="text-lg font-semibold tabular-nums" dir="ltr">
                {formatDuration(meter.seconds)}
              </span>
            </div>

            {meter.remaining !== null && (
              <div className="mt-1 flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-xs text-ink-muted">
                  {t('resource.remaining')}
                </span>
                <span
                  className={[
                    'tabular-nums',
                    overdue
                      ? 'font-semibold text-red-600 dark:text-red-400'
                      : expiring
                        ? 'font-semibold text-amber-600'
                        : '',
                    expiring && !overdue && !reducedMotion && meter.running ? 'animate-pulse' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  dir="ltr"
                >
                  {formatDuration(meter.remaining)}
                </span>
              </div>
            )}

            {progress !== null && (
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-line">
                <div
                  className={
                    overdue
                      ? 'h-full bg-red-500'
                      : expiring
                        ? 'h-full bg-amber-500'
                        : 'h-full bg-accent-solid'
                  }
                  style={{ inlineSize: `${progress}%` }}
                />
              </div>
            )}

            {/* P10-08 — time a package already paid for is never charged again. */}
            {meter.entitlement && (
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs">
                <span className="flex items-center gap-1.5 text-accent">
                  <Ticket className="h-3.5 w-3.5" />
                  {meter.entitlement.packageName ?? t('spaces.resources.entitlement.aPackage')}
                </span>
                <span className="tabular-nums text-accent" dir="ltr">
                  {t('spaces.resources.entitlement.coveredShort', {
                    covered: formatDuration(meter.coveredSeconds),
                  })}
                </span>
              </div>
            )}

            <div className="mt-2 flex flex-wrap items-baseline justify-between gap-2">
              <span className="flex items-center gap-1.5 text-xs text-ink-muted">
                <CircleDollarSign className="h-3.5 w-3.5" />
                {meter.ruleName ?? t('spaces.resources.rule')}
              </span>
              <span className="font-semibold">
                {meter.amount === null
                  ? t('spaces.resources.noPricing')
                  : formatCurrency(meter.amount, currency, i18n.language)}
              </span>
            </div>
          </div>
        )}

        {/* A shared area (kids zone) shows one row per child, each with its own
            timer, its own package coverage and its own check-out. */}
        {shared && sessions.length > 0 && (
          <ul className="divide-y rounded-lg border">
            {sessions.map((session) => {
              const rowMeter = meterFor(session, now);
              return (
                <li key={session.id} className="space-y-2 p-3">
                  <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
                    <span className="font-medium">
                      {children[session.childId ?? '']?.displayName ??
                        t('spaces.resources.guest')}
                      {children[session.childId ?? '']?.tagCode && (
                        <span className="ms-2 text-xs text-ink-muted" dir="ltr">
                          {children[session.childId ?? '']?.tagCode}
                        </span>
                      )}
                    </span>
                    <span className="tabular-nums" dir="ltr">
                      {formatDuration(rowMeter.seconds)}
                      {rowMeter.remaining !== null && (
                        <span
                          className={
                            rowMeter.remaining <= EXPIRING_SECONDS
                              ? 'ms-2 font-semibold text-amber-600'
                              : 'ms-2 text-ink-muted'
                          }
                        >
                          −{formatDuration(rowMeter.remaining)}
                        </span>
                      )}
                    </span>
                  </div>
                  {isKids && canReadChildren && (
                    <p className="flex items-center gap-1.5 text-xs text-ink-muted">
                      <Shield className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      {(() => {
                        const on = children[session.childId ?? '']?.guardians ?? [];
                        if (!on.length) return t('spaces.kids.noGuardian');
                        return t('spaces.kids.withGuardian', {
                          name: on.map((link) => link.guardian.name).join(', '),
                        });
                      })()}
                    </p>
                  )}
                  <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                    <span className="flex items-center gap-2">
                      {session.status === 'PAUSED' && (
                        <Badge tone="warning">{t('spaces.resources.status.PAUSED')}</Badge>
                      )}
                      {rowMeter.entitlement && (
                        <Badge tone="brand">
                          {t('spaces.resources.entitlement.coveredShort', {
                            covered: formatDuration(rowMeter.coveredSeconds),
                          })}
                        </Badge>
                      )}
                    </span>
                    <span className="font-semibold">
                      {rowMeter.amount === null
                        ? t('spaces.resources.noPricing')
                        : formatCurrency(rowMeter.amount, currency, i18n.language)}
                    </span>
                  </div>
                  {actionsFor(session)}
                </li>
              );
            })}
          </ul>
        )}

        {/* WHICH RATE CARD prices this, and a way into it — not a rule editor.
            A rate belongs to a card that ten consoles share, so editing one here
            would be the per-resource shape back again under a new name, and the
            manager would have no way to tell what else they had just repriced.
            Naming the card instead is also how a venue notices from the board
            that nine consoles are on one card and the tenth is on none. */}
        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
            <span className="flex items-center gap-1.5 text-ink-muted">
              <CircleDollarSign className="h-3.5 w-3.5" aria-hidden />
              {t('spaces.resources.pricing')}
            </span>
            {attachedList ? (
              onPricing ? (
                <button
                  type="button"
                  onClick={onPricing}
                  className="text-accent hover:underline"
                  dir="auto"
                >
                  {attachedList.name}
                </button>
              ) : (
                <span dir="auto">{attachedList.name}</span>
              )
            ) : (
              <span className="font-medium text-amber-700 dark:text-amber-300">
                {t('spaces.pricingRules.lists.notAttached')}
              </span>
            )}
          </div>

          {/* A console on no card cannot start a timer at all, so the sentence
              that reports it carries the way out rather than merely stating it. */}
          {!attachedList && onPricing && can('resources.update') && (
            <Button size="sm" variant="outline" onClick={onPricing}>
              <CircleDollarSign className="h-4 w-4" />
              {t('spaces.pricingRules.lists.attachAction')}
            </Button>
          )}

          {attachedList && (
            <>
              <button
                type="button"
                onClick={() => setShowRules((value) => !value)}
                aria-expanded={showRules}
                className="flex items-center gap-1 text-xs text-accent hover:underline"
              >
                <ChevronDown className={showRules ? 'h-3.5 w-3.5 rotate-180' : 'h-3.5 w-3.5'} />
                {t('spaces.pricingRules.lists.showRates')}
              </button>
              {showRules && (
                <div className="mt-2 space-y-2">
                  {listQuery.isLoading ? (
                    <p className="text-xs text-ink-muted">{t('common.loading')}</p>
                  ) : (
                    <PricingRuleList
                      rules={listQuery.data?.rules ?? []}
                      currency={currency}
                      activeRuleId={primary?.pricingRule?.id ?? primary?.billing?.ruleId ?? null}
                      /* An empty card is the second of the three start refusals,
                         met here before anyone sits down rather than at the desk. */
                      emptyAction={
                        onPricing && can('resources.create') ? (
                          <Button size="sm" onClick={onPricing}>
                            <CircleDollarSign className="h-4 w-4" />
                            {t('spaces.pricingRules.addFirst')}
                          </Button>
                        ) : undefined
                      }
                    />
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {isKids && (queue.tickets.length > 0 || occupied >= capacity) && (
          <div className="space-y-2">
            <p className="text-xs font-medium">{t('spaces.kids.queueTitle')}</p>
            <KidsWaitingList
              resourceId={resource.id}
              tickets={queue.tickets}
              loading={queue.loading}
              free={kidsOccupancy?.free ?? Math.max(0, capacity - occupied)}
              onChanged={() => {
                queue.reload();
                onRefresh();
              }}
            />
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {can('resource_sessions.start') && (occupied === 0 || shared) && (
            <Button size="sm" onClick={onStart} disabled={!startable || busy}>
              <Play className="h-4 w-4" />
              {shared ? t('spaces.resources.checkIn') : t('resource.start')}
            </Button>
          )}
          {isKids && onCheckOutByTag && can('resource_sessions.stop') && sessions.length > 0 && (
            <Button size="sm" variant="outline" onClick={onCheckOutByTag} disabled={busy}>
              <ScanLine className="h-4 w-4" />
              {t('spaces.kids.checkOutByTag')}
            </Button>
          )}
          {!shared && primary && actionsFor(primary)}
          {/* C-16 — editing the RESOURCE, not the session. Kept last so a
              running meter's controls stay under the thumb. */}
          {onEdit && can('resources.update') && (
            <Button size="sm" variant="outline" onClick={onEdit} disabled={busy}>
              <Pencil className="h-4 w-4" />
              {t('common.edit')}
            </Button>
          )}
          {onRetire && can('resources.delete') && (
            <Button size="sm" variant="outline" onClick={onRetire} disabled={busy}>
              <Archive className="h-4 w-4" />
              {t('spaces.resourceForm.retire')}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
